import { type ImgHTMLAttributes, useEffect, useState } from 'react'
import { Building2, FileText } from 'lucide-react'
import { cn } from '../../lib/cn'
import { apiAssetUrl } from '../../config'

/** Two-letter style initials for placeholders. */
export function initialsFromLabel(s: string, maxChars = 2): string {
  const t = s.trim()
  if (!t) return '—'
  const parts = t.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, maxChars).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function resolveUrl(src: string | null | undefined): string {
  if (src == null) return ''
  const s = String(src).trim()
  if (!s) return ''
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:') || s.startsWith('blob:')) return s
  return apiAssetUrl(s)
}

export type SafeImageTone = 'default' | 'hero' | 'logo' | 'document' | 'brand'

/**
 * Image with load/error handling: missing URL or failed fetch shows a clear placeholder (initials, icon, or label).
 */
export function SafeImage({
  src,
  alt,
  className,
  imgClassName,
  label: labelProp,
  tone = 'default',
  loading,
  width,
  height,
  ...imgRest
}: Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src: string | null | undefined
  /** Shown in fallback; defaults to alt */
  label?: string
  tone?: SafeImageTone
  imgClassName?: string
}) {
  const [failed, setFailed] = useState(false)
  const label = (labelProp ?? alt ?? '').trim()
  const url = resolveUrl(src)
  const showImg = Boolean(url) && !failed

  useEffect(() => {
    setFailed(false)
  }, [src])

  if (!showImg) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-1 text-center',
          tone === 'brand' && 'bg-transparent',
          tone === 'document' && 'bg-slate-100 dark:bg-slate-800',
          tone === 'logo' && 'bg-transparent',
          tone === 'default' && 'bg-slate-100 dark:bg-slate-800',
          tone === 'hero' &&
            'bg-gradient-to-br from-primary/25 via-slate-100 to-accent/20 dark:from-primary/35 dark:via-navy-900 dark:to-accent/25',
          className
        )}
        role="img"
        aria-label={label ? `${label} — image unavailable` : 'Image unavailable'}
      >
        {tone === 'brand' ? (
          <span className="font-display text-lg font-bold tracking-tight text-primary dark:text-accent sm:text-xl">
            {label || 'DigiSutra'}
          </span>
        ) : tone === 'logo' ? (
          <>
            <Building2 className="h-8 w-8 shrink-0 text-slate-400 dark:text-slate-500 sm:h-9 sm:w-9" aria-hidden />
            {label ? (
              <span className="max-w-full truncate px-1 text-[10px] font-semibold text-slate-600 dark:text-slate-300 sm:text-xs">
                {label}
              </span>
            ) : null}
          </>
        ) : tone === 'document' ? (
          <>
            <FileText className="text-primary dark:text-accent opacity-55" size={36} strokeWidth={1.8} aria-hidden />
            {label ? (
              <span className="line-clamp-2 max-w-full px-2 text-xs font-medium text-slate-600 dark:text-slate-400">{label}</span>
            ) : null}
          </>
        ) : tone === 'hero' ? (
          <>
            <span className="text-3xl font-bold tracking-tight text-white drop-shadow-sm sm:text-4xl" aria-hidden>
              {initialsFromLabel(label || alt || '—')}
            </span>
            {label ? (
              <span className="line-clamp-2 max-w-full px-2 text-xs font-medium text-white/85 sm:text-sm">{label}</span>
            ) : null}
          </>
        ) : (
          <>
            <span className="text-xl font-bold tracking-tight text-slate-500 dark:text-slate-400 sm:text-2xl" aria-hidden>
              {initialsFromLabel(label || alt || '—')}
            </span>
            {label ? (
              <span className="line-clamp-2 max-w-full px-2 text-[11px] font-medium text-slate-500 dark:text-slate-500 sm:text-xs">
                {label}
              </span>
            ) : null}
          </>
        )}
      </div>
    )
  }

  return (
    <img
      src={url}
      alt={alt}
      loading={loading}
      width={width}
      height={height}
      className={cn(imgClassName ?? className)}
      onError={() => setFailed(true)}
      {...imgRest}
    />
  )
}
